import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs))
}

/**
 * Copy text to clipboard. Works on HTTP and inside Radix Dialog focus traps.
 * Tries navigator.clipboard first (HTTPS), then falls back to execCommand
 * with a textarea appended INSIDE the active dialog to stay within focus trap.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
	// 1. Modern API (requires secure context / HTTPS)
	if (navigator.clipboard && window.isSecureContext) {
		try {
			await navigator.clipboard.writeText(text);
			return true;
		} catch {
			// fall through to fallback
		}
	}

	// 2. Fallback: textarea inside the active dialog (avoids Radix focus-trap issues)
	const textarea = document.createElement("textarea");
	textarea.value = text;
	textarea.setAttribute("readonly", "");
	textarea.style.position = "fixed";
	textarea.style.left = "0";
	textarea.style.top = "0";
	textarea.style.width = "1px";
	textarea.style.height = "1px";
	textarea.style.padding = "0";
	textarea.style.border = "none";
	textarea.style.outline = "none";
	textarea.style.boxShadow = "none";
	textarea.style.opacity = "0";

	// Append inside the dialog so Radix focus-trap allows .focus()
	const dialog = document.querySelector('[role="dialog"]');
	const container = dialog || document.body;
	container.appendChild(textarea);

	textarea.focus();
	textarea.select();
	textarea.setSelectionRange(0, text.length);

	let success = false;
	try {
		success = document.execCommand("copy");
	} catch {
		success = false;
	}

	container.removeChild(textarea);
	return success;
}
